import { Box, Alert, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { AdminOfflineGuard } from '@components/guards/AdminOfflineGuard';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useAssessmentConfigData } from './hooks';
import { AssessmentConfigHeader, AssessmentConfigContent } from './components';

export const EquipmentAssessmentConfig = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();

  const {
    formData,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving,
    saveError,
    validationErrors,
    updateField,
    saveChanges,
    discardChanges
  } = useAssessmentConfigData();

  const handleSave = async () => {
    await saveChanges();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isOnline) {
    return <AdminOfflineGuard children={null} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t(
            'settings:equipmentAssessment.errors.loadFailed',
            'Failed to load equipment assessment configuration'
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.palette.background.default
      }}
    >
      <AssessmentConfigHeader
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onSave={handleSave}
        onDiscard={discardChanges}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0
        }}
      >
        {saveError && (
          <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2 }}>
            <Alert severity="error">
              {t(
                'settings:equipmentAssessment.errors.saveFailed',
                'Failed to save configuration'
              )}
              : {saveError.message}
            </Alert>
          </Box>
        )}

        <AssessmentConfigContent
          formData={formData}
          validationErrors={validationErrors}
          onUpdateField={updateField}
        />
      </Box>
    </Box>
  );
};
